import React, { useState } from "react";
import styles from "./ContentForm.module.scss";
import { RequestState } from "../../utils/RequestState";
import LoadingSpinner from "../LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormInput from "./FormInput";
import { FieldState, ValType } from "../../utils/ValidationFunctions";
import AgreementCheckBox from "./AgreementCheckBox";
import SonicLink from "../routing/SonicLink";

export type ContentFormField = {
  id: string;
  name: string;
  label: string;
  type: "text" | "textarea";
  required: boolean;
  valType?: ValType;
  rows?: number;
};

type Props = {
  headTitle: string;
  bodyTitle: { label: string; value: string };
  requiredFields: Array<ContentFormField>;
  requestState: RequestState;
  submitHandler: (fieldState: FieldState) => void;
  closeHandler: () => void;
  successMessage: string;
  subSuccessMessage?: string;
  privacyPolicyCheck?: boolean;
};
const ContentForm: React.FC<Props> = ({
  headTitle,
  bodyTitle,
  requiredFields,
  requestState,
  submitHandler,
  closeHandler,
  successMessage,
  subSuccessMessage,
  privacyPolicyCheck = false,
}) => {
  const initFormState = () => {
    const initState: FieldState = {};

    for (const input of requiredFields) {
      if (input.valType === ValType.NotNull) {
        initState[input.name] = ["", true];
      } else {
        initState[input.name] = ["", false];
      }
    }

    return initState;
  };

  const [formState, setFormState] = useState<FieldState>(initFormState());
  const [isAgreement, setIsAgreement] = useState<boolean>(false);

  const changeHandler = (value: string | File | undefined, name: string) => {
    const fieldState = formState;

    if (value !== undefined) {
      fieldState[name] = [value, false];
    } else {
      fieldState[name] = ["", true];
    }

    setFormState({ ...fieldState });
  };

  const handleSubmit = () => {
    if (privacyPolicyCheck && !isAgreement) {
      return;
    }

    submitHandler(formState);
  };

  return (
    <div className={styles["c-form-modal-wrapper"]}>
      <div className={styles["c-form-modal"]}>
        {requestState == RequestState.Doing ? (
          <LoadingSpinner space={1} />
        ) : requestState === RequestState.Waiting ? (
          <>
            <div className={styles["c-form-modal__header"]}>
              <span>{headTitle}</span>
              <button
                type="button"
                onClick={closeHandler}
                className={styles["c-form-modal__header__close-btn"]}
              >
                <FontAwesomeIcon icon={["fas", "times"]} />
              </button>
            </div>

            <div className={styles["c-form-modal__body"]}>
              <div>
                <div className={styles["c-form-modal__small-text"]}>
                  {bodyTitle.label}
                </div>
                <div>{bodyTitle.value}</div>
              </div>

              {requiredFields.map((input, i) => (
                <div
                  key={i}
                  className={`${styles["c-form-modal__input-field"]}`}
                >
                  <FormInput
                    id={input.id}
                    name={input.name}
                    type={input.type}
                    label={input.label}
                    label_class={
                      input.required
                        ? `${styles["c-form-modal__small-text"]} ${styles["required-mark"]}`
                        : styles["c-form-modal__small-text"]
                    }
                    handler={changeHandler}
                    rows={input.rows}
                    val_type={input.valType}
                  />
                </div>
              ))}

              {privacyPolicyCheck ? (
                <div>
                  <AgreementCheckBox
                    value={isAgreement}
                    handler={() => setIsAgreement(!isAgreement)}
                  >
                    <SonicLink to="/info/privacy/" newTab={true}>
                      プライバシーポリシー
                    </SonicLink>
                    に同意して送信
                  </AgreementCheckBox>
                </div>
              ) : null}
            </div>
          </>
        ) : requestState === RequestState.Failed ? (
          <p className={styles["sent-message"]}>
            {`送信に失敗しました。\nお手数ですが、再度お試しください。`}
          </p>
        ) : (
          <p className={styles["sent-message"]}>
            {successMessage}
            <span>{subSuccessMessage}</span>
          </p>
        )}

        {requestState !== RequestState.Doing ? (
          <div>
            {requestState === RequestState.Waiting ? (
              <button
                type="button"
                onClick={handleSubmit}
                className={styles["c-form-modal__action-btn"]}
              >
                送信
              </button>
            ) : (
              <button
                type="button"
                onClick={closeHandler}
                className={styles["c-form-modal__action-btn"]}
              >
                閉じる
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ContentForm;
